import { Store } from "@tanstack/store";
import { parseFunctionOrValue } from "./utils.js";
function getDefaultAsyncQueuerState() {
  return structuredClone({
    activeItems: [],
    errorCount: 0,
    expirationCount: 0,
    isEmpty: true,
    isFull: false,
    isIdle: true,
    isRunning: true,
    itemTimestamps: [],
    items: [],
    lastResult: null,
    pendingTick: false,
    rejectionCount: 0,
    settledCount: 0,
    size: 0,
    status: "idle",
    successCount: 0
  });
}
const defaultOptions = {
  addItemsTo: "back",
  concurrency: 1,
  expirationDuration: Infinity,
  getIsExpired: () => false,
  getItemsFrom: "front",
  getPriority: (item) => item?.priority ?? 0,
  initialItems: [],
  maxSize: Infinity,
  started: true,
  wait: 0
};
class AsyncQueuer {
  constructor(fn, initialOptions = {}) {
    this.fn = fn;
    this.store = new Store(getDefaultAsyncQueuerState());
    this.#timeoutIds = /* @__PURE__ */ new Set();
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const { activeItems, items, isRunning } = combinedState;
        const size = items.length;
        const isFull = size >= (this.options.maxSize ?? Infinity);
        const isEmpty = size === 0;
        const isIdle = isRunning && isEmpty && activeItems.length === 0;
        const status = isIdle ? "idle" : isRunning ? "running" : "stopped";
        return {
          ...combinedState,
          isEmpty,
          isFull,
          isIdle,
          size,
          status
        };
      });
    };
    this.#getWait = () => {
      return parseFunctionOrValue(this.options.wait ?? 0, this);
    };
    this.#getConcurrency = () => {
      return parseFunctionOrValue(this.options.concurrency ?? 1, this);
    };
    this.#tick = () => {
      if (!this.store.state.isRunning) {
        this.#setState({ pendingTick: false });
        return;
      }
      this.#setState({ pendingTick: true });
      this.#checkExpiredItems();
      const activeItems = this.store.state.activeItems;
      while (activeItems.length < this.#getConcurrency() && !this.store.state.isEmpty) {
        const nextItem = this.peekNextItem();
        if (!nextItem) {
          break;
        }
        activeItems.push(nextItem);
        this.#setState({
          activeItems
        });
        (async () => {
          const result = await this.execute();
          this.#setState({ lastResult: result });
          const wait = this.#getWait();
          if (wait > 0) {
            const timeoutId = setTimeout(() => this.#tick(), wait);
            this.#timeoutIds.add(timeoutId);
            return;
          }
          this.#tick();
        })();
      }
      this.#setState({ pendingTick: false });
    };
    this.addItem = (item, position = this.options.addItemsTo ?? "back", runOnItemsChange = true) => {
      if (this.store.state.isFull) {
        this.#setState({
          rejectionCount: this.store.state.rejectionCount + 1
        });
        this.options.onReject?.(item, this);
        return false;
      }
      const priority = this.options.getPriority !== defaultOptions.getPriority ? this.options.getPriority(item) : item.priority;
      const items = this.store.state.items;
      const itemTimestamps = this.store.state.itemTimestamps;
      if (priority !== void 0) {
        const insertIndex = items.findIndex((existing) => {
          const existingPriority = this.options.getPriority !== defaultOptions.getPriority ? this.options.getPriority(existing) : existing.priority;
          return existingPriority < priority;
        });
        if (insertIndex === -1) {
          items.push(item);
          itemTimestamps.push(Date.now());
        } else {
          items.splice(insertIndex, 0, item);
          itemTimestamps.splice(insertIndex, 0, Date.now());
        }
      } else {
        if (position === "front") {
          items.unshift(item);
          itemTimestamps.unshift(Date.now());
        } else {
          items.push(item);
          itemTimestamps.push(Date.now());
        }
      }
      this.#setState({
        items,
        itemTimestamps
      });
      if (runOnItemsChange) {
        this.options.onItemsChange?.(this);
      }
      if (this.store.state.isRunning && !this.store.state.pendingTick) {
        this.#tick();
      }
      return true;
    };
    this.getNextItem = (position = this.options.getItemsFrom ?? "front") => {
      const { items, itemTimestamps } = this.store.state;
      let item;
      if (position === "front") {
        item = items[0];
        if (item !== void 0) {
          this.#setState({
            items: items.slice(1),
            itemTimestamps: itemTimestamps.slice(1)
          });
        }
      } else {
        item = items[items.length - 1];
        if (item !== void 0) {
          this.#setState({
            items: items.slice(0, -1),
            itemTimestamps: itemTimestamps.slice(0, -1)
          });
        }
      }
      if (item !== void 0) {
        this.options.onItemsChange?.(this);
      }
      return item;
    };
    this.execute = async (position) => {
      const item = this.getNextItem(position);
      if (item !== void 0) {
        try {
          const lastResult = await this.fn(item);
          this.#setState({
            successCount: this.store.state.successCount + 1,
            lastResult
          });
          this.options.onSuccess?.(lastResult, this);
        } catch (error) {
          this.#setState({
            errorCount: this.store.state.errorCount + 1
          });
          this.options.onError?.(error, this);
          if (this.options.throwOnError) {
            throw error;
          }
        } finally {
          this.#setState({
            activeItems: this.store.state.activeItems.filter(
              (activeItem) => activeItem !== item
            ),
            settledCount: this.store.state.settledCount + 1
          });
          this.options.onSettled?.(this);
        }
      }
      return item;
    };
    this.flush = (numberOfItems = this.store.state.items.length, position) => {
      this.#clearTimeouts();
      for (let i = 0; i < numberOfItems; i++) {
        this.execute(position);
      }
    };
    this.#checkExpiredItems = () => {
      if ((this.options.expirationDuration ?? Infinity) === Infinity && this.options.getIsExpired === defaultOptions.getIsExpired) {
        return;
      }
      const now = Date.now();
      const expiredIndices = [];
      for (let i = 0; i < this.store.state.size; i++) {
        const timestamp = this.store.state.itemTimestamps[i];
        if (timestamp === void 0) continue;
        const item = this.store.state.items[i];
        if (item === void 0) continue;
        const isExpired = this.options.getIsExpired !== defaultOptions.getIsExpired ? this.options.getIsExpired(item, timestamp) : now - timestamp > (this.options.expirationDuration ?? Infinity);
        if (isExpired) {
          expiredIndices.push(i);
        }
      }
      for (let i = expiredIndices.length - 1; i >= 0; i--) {
        const index = expiredIndices[i];
        if (index === void 0) continue;
        const expiredItem = this.store.state.items[index];
        if (expiredItem === void 0) continue;
        const newItems = [...this.store.state.items];
        const newTimestamps = [...this.store.state.itemTimestamps];
        newItems.splice(index, 1);
        newTimestamps.splice(index, 1);
        this.#setState({
          items: newItems,
          itemTimestamps: newTimestamps,
          expirationCount: this.store.state.expirationCount + 1
        });
        this.options.onExpire?.(expiredItem, this);
      }
      if (expiredIndices.length > 0) {
        this.options.onItemsChange?.(this);
      }
    };
    this.peekNextItem = (position = "front") => {
      if (position === "front") {
        return this.store.state.items[0];
      }
      return this.store.state.items[this.store.state.size - 1];
    };
    this.peekAllItems = () => {
      return [...this.peekActiveItems(), ...this.peekPendingItems()];
    };
    this.peekActiveItems = () => {
      return [...this.store.state.activeItems];
    };
    this.peekPendingItems = () => {
      return [...this.store.state.items];
    };
    this.start = () => {
      this.#setState({ isRunning: true });
      if (!this.store.state.pendingTick && !this.store.state.isEmpty) {
        this.#tick();
      }
    };
    this.stop = () => {
      this.#clearTimeouts();
      this.#setState({ isRunning: false, pendingTick: false });
    };
    this.#clearTimeouts = () => {
      this.#timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
      this.#timeoutIds.clear();
    };
    this.clear = () => {
      this.#setState({ items: [], itemTimestamps: [] });
      this.options.onItemsChange?.(this);
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncQueuerState());
      this.options.onItemsChange?.(this);
    };
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? !initialOptions.onError
    };
    const isInitiallyRunning = this.options.initialState?.isRunning ?? this.options.started ?? true;
    this.#setState({
      ...this.options.initialState,
      isRunning: isInitiallyRunning
    });
    if (this.options.initialState?.items) {
      if (this.store.state.isRunning) {
        this.#tick();
      }
    } else {
      for (let i = 0; i < (this.options.initialItems?.length ?? 0); i++) {
        const item = this.options.initialItems[i];
        const isLast = i === (this.options.initialItems?.length ?? 0) - 1;
        this.addItem(item, this.options.addItemsTo ?? "back", isLast);
      }
    }
  }
  #timeoutIds;
  #setState;
  #getWait;
  #getConcurrency;
  #tick;
  #checkExpiredItems;
  #clearTimeouts;
}
function asyncQueue(fn, initialOptions) {
  const asyncQueuer = new AsyncQueuer(fn, initialOptions);
  return asyncQueuer.addItem;
}
export {
  AsyncQueuer,
  asyncQueue
};
//# sourceMappingURL=async-queuer.js.map
